import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  RotateCcw,
  Zap,
} from "lucide-react";
import { heroStateFor } from "@/components/agents/agentHeroState";
import { tp } from "@/lib/plural";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useSidebar } from "@/components/ui/sidebar";
import { shouldAutoOpenConfigDrawer } from "@/lib/agent-config-drawer";
import { AgentInputForm } from "./AgentInputForm";
import { AgentExecutionProvider } from "./AgentExecutionContext";
import { ExecutionStepper } from "./ExecutionStepper";
import { StreamingOutput } from "./StreamingOutput";
import { ExecutionMetrics } from "./ExecutionMetrics";
import { AgentStatusBanner } from "./AgentStatusBanner";
import { AgentLockBanner } from "./AgentLockBanner";
import { ArtifactChain } from "./ArtifactChain";
import { ExecutionHistory } from "./ExecutionHistory";
import { ScanHistory } from "./ScanHistory";
import { HeroSection } from "@/components/brand/HeroSection";
import { HeroOverline } from "@/components/brand/HeroOverline";
import { CompactAgentGlyph } from "@/components/brand/CompactAgentGlyph";
import {
  PipelineProgress,
  type PipelineNode,
  type NodeStatus as PipelineNodeStatus,
} from "@/components/agents/functional/PipelineProgress";
import {
  FloatingChat,
  type ChatMode,
} from "@/components/agents/functional/patternC/FloatingChat";
import { usePipelineStatus, useAgentSchema, usePreparedInputs, useExecutions } from "@/hooks/useApi";
import { useAgentExecution } from "@/hooks/useAgentExecution";
import { resolveWarnings } from "@/components/agents/resolveWarnings";
import { WarningsBanner } from "@/components/agents/WarningsBanner";
import { formatDateTime } from "@/lib/datetime";
import type { AgentPipelineStatus } from "@/types/api";

/**
 * Layout de workbench: o formulário de configuração mora num <Sheet> lateral
 * e as saídas ocupam a largura toda. Usado pelo Architect, no visual do
 * Functional Workbench (docs/decisions/2026-05-25-fa-ux-convergence-with-functional.md).
 *
 * Até a #1044 havia também a variante "default" (formulário inline à esquerda,
 * saídas à direita e a MigrationSpine na lateral), que nenhuma tela usava mais.
 */
interface AgentPageLayoutProps {
  agentId: string; // backend agent ID (e.g., "architect")
  agentName: string;
  description: string;
  children: React.ReactNode;
  phases?: string[];
  /** When true, the Output is NOT rendered here — the children handle it via tabs. */
  renderOutputInTabs?: boolean;
  /** Ordinal of this agent in the pipeline (e.g. 2 for FA). */
  agentNumber?: number;
  /** Total number of agents shown in the overline ("02 OF 07"). */
  totalAgents?: number;
  /** Small caps badge next to the title (e.g. "Clean Core Catalog"). */
  agentBadge?: string;
  /** SAP Activate phase label shown in the hero meta line. */
  phaseLabel?: string;
  /**
   * Trabalho que roda FORA da execução do agente e que o cabeçalho precisa
   * refletir (#853). No Architect é a varredura da base, que é job próprio e
   * dura horas: sem isto o hero dizia `COMPLETED` e o banner "Last execution
   * completed successfully" ao lado de uma barra andando.
   *
   * O rótulo já vem pronto e traduzido de quem sabe o que está rodando.
   */
  backgroundActivity?: string | null;
}

const statusMap: Record<AgentPipelineStatus, "In Progress" | "Complete" | "Paused" | "Not Started"> = {
  completed: "Complete",
  running: "In Progress",
  // "Paused" é o mais próximo do vocabulário desta tela: a run acabou, quem
  // está parado é o projeto, à espera da decisão de escopo (#854).
  awaiting_decision: "Paused",
  ready: "Not Started",
  locked: "Not Started",
  failed: "Not Started",
};

export function AgentPageLayout({
  agentId,
  agentName,
  description,
  children,
  phases,
  renderOutputInTabs = false,
  backgroundActivity = null,
  agentNumber,
  totalAgents,
  agentBadge,
  phaseLabel,
}: AgentPageLayoutProps) {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { data: pipeline } = usePipelineStatus(projectId);
  const { data: schema } = useAgentSchema(projectId, agentId);
  const { data: preparedInputs } = usePreparedInputs(projectId, agentId);
  const { data: executions = [], isLoading: executionsLoading } = useExecutions(projectId, agentId);

  const execution = useAgentExecution();

  // Past run selection: null = latest/current
  const [selectedExecutionId, setSelectedExecutionId] = useState<string | null>(null);

  // FA full-base: varredura selecionada no histórico (null = consolidado
  // cumulativo). Compartilhada via contexto com as abas (que filtram o
  // catálogo por ela) e com o ScanHistory (que a marca/dispara).
  const [selectedScanId, setSelectedScanId] = useState<string | null>(null);
  const { t } = useTranslation();

  // Config form lives in a Sheet.
  const [configSheetOpen, setConfigSheetOpen] = useState(false);
  // Floating chat mode (closed by default).
  const [chatMode, setChatMode] = useState<ChatMode>("closed");

  // Collapse the global AppSidebar on mount so the workbench gets the same
  // horizontal real-estate the Functional Workbench has (which mounts its own
  // SidebarProvider with defaultOpen={false}). On unmount we restore — so
  // navigating back to a project page brings the sidebar back expanded.
  const sidebar = useSidebar();
  useEffect(() => {
    const wasOpen = sidebar.open;
    sidebar.setOpen(false);
    return () => {
      sidebar.setOpen(wasOpen);
    };
    // We intentionally don't depend on `sidebar` — the setter is stable and
    // we want this to fire once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pipelineAgent = pipeline?.agents.find(a => a.agent_id === agentId);
  const isLocked = pipelineAgent?.pipeline_status === "locked";
  const pipelineStatus = pipelineAgent?.pipeline_status || "locked";
  const hasCompletedExecution = executions.some(e => e.status === "completed");
  const isComplete = pipelineStatus === "completed" || hasCompletedExecution;
  const isRunning = execution.status === "running";
  const executionDone = execution.status === "completed";

  // When a new execution starts, reset to latest
  const effectiveSelectedId = isRunning ? null : selectedExecutionId;

  // Resolve the selected execution
  const latestCompleted = executions.find(e => e.status === "completed");
  const selectedExecution = effectiveSelectedId
    ? executions.find(e => e.id === effectiveSelectedId)
    : null;
  const isViewingPastRun = !!selectedExecution && selectedExecution.id !== latestCompleted?.id;

  // Pré-carrega o form de "New Run" com os inputs da última execução (a mais
  // recente com config), pra o usuário re-rodar/ajustar sem redigitar tudo.
  // Os inputs derivados de upstream (preparedInputs) ainda têm precedência —
  // só sobrescrevem quando trazem valor (não clobberam com vazio).
  const formPreparedInputs = useMemo(() => {
    const lastRun =
      (executions.find(
        (e) => e.input_config && Object.keys(e.input_config).length > 0,
      )?.input_config as Record<string, unknown> | undefined) || {};
    const merged: Record<string, unknown> = { ...lastRun };
    for (const [k, v] of Object.entries(preparedInputs?.inputs || {})) {
      if (v !== "" && v != null) merged[k] = v;
    }
    return merged;
  }, [executions, preparedInputs]);

  // Resolve outputs: past run selection > live execution > latest completed
  const resolvedOutputs = isViewingPastRun
    ? (selectedExecution?.output_summary as Record<string, unknown> | null)
    : (execution.outputs
      ?? (latestCompleted?.output_summary as Record<string, unknown> | null)
      ?? null);

  const resolvedMetrics = isViewingPastRun
    ? (selectedExecution?.metrics || null)
    : (execution.metrics || latestCompleted?.metrics || null);

  const resolvedExecutionId = isViewingPastRun
    ? (selectedExecution?.id ?? null)
    : (execution.executionId ?? latestCompleted?.id ?? null);

  // #650: o `useAgentExecution` monta `warnings` só de eventos SSE, então no
  // reload o banner ficava vazio mesmo com a execução tendo avisos gravados.
  // Mesma precedência de `resolvedOutputs` e `resolvedMetrics`.
  const resolvedWarnings = resolveWarnings({
    live: execution.warnings,
    selected: selectedExecution as { warnings?: unknown } | null,
    latestCompleted: latestCompleted as { warnings?: unknown } | null,
    isViewingPastRun,
  });

  const showOutput = isRunning || executionDone || isComplete || isViewingPastRun;

  // #853: o cabeçalho e o banner passam a conhecer o trabalho de fora da
  // execução. O helper decide QUEM manda; os textos ricos da execução (a fase
  // corrente, a data da run passada) continuam sendo montados aqui.
  const heroState = heroStateFor(
    { isRunning, isComplete, isLocked, executionDone, status: execution.status },
    backgroundActivity,
  );
  const backgroundTookOver = heroState.source === "background";

  const status = backgroundTookOver ? heroState.bannerStatus
    : isRunning ? "In Progress"
    : executionDone || isComplete ? "Complete"
    : statusMap[pipelineStatus] || "Not Started";

  // O `heroState` segue devolvendo os textos em inglês, que é o que o teste
  // dele compara; a tradução acontece aqui, na hora de mostrar. O detalhe da
  // varredura em segundo plano já chega traduzido pelo Architect.
  const statusDetail = isRunning ? t("Executing — {{phase}}...", { phase: execution.phaseMessage || execution.phase || t("processing") })
    : isViewingPastRun ? t("Viewing past run from {{date}}", { date: formatDateTime(selectedExecution!.started_at) })
    : backgroundTookOver ? heroState.bannerDetail
    : isComplete ? t("Last execution completed successfully")
    : isLocked ? t("Waiting for upstream agents to complete")
    : t("Ready to run");

  const reportContent = execution.tokens || (resolvedOutputs?.report as string) || "";

  const executionContextValue = useMemo(() => ({
    outputs: resolvedOutputs,
    status: execution.status,
    executionId: resolvedExecutionId,
    isRunning,
    isComplete: isComplete || executionDone || isViewingPastRun,
    tokens: execution.tokens,
    metrics: resolvedMetrics,
    warnings: resolvedWarnings,
    projectId: projectId || undefined,
    agentId,
    showOutput,
    reportContent,
    selectedExecutionId: effectiveSelectedId,
    isViewingPastRun,
    selectedScanId,
    setSelectedScanId,
  }), [resolvedOutputs, execution.status, resolvedExecutionId, isRunning, isComplete, executionDone, execution.tokens, resolvedMetrics, resolvedWarnings, projectId, agentId, showOutput, reportContent, effectiveSelectedId, isViewingPastRun, selectedScanId]);

  const handleRun = useCallback((formData: Record<string, unknown>) => {
    if (!projectId) return;
    setSelectedExecutionId(null); // Reset to latest when running new
    setConfigSheetOpen(false); // close drawer when run starts
    execution.execute(projectId, agentId, {
      message: `Run ${agentName} analysis`,
      context: formData,
    });
  }, [projectId, agentId, agentName, execution]);

  const handleCancel = useCallback(() => {
    execution.cancel();
  }, [execution]);

  // When the user lands on the page with no previous executions and the agent is ready to run, surface the config drawer
  // immediately as a call-to-action. Doesn't fire while a run is in flight
  // or while viewing a past run.
  //
  // Crucial: só decidimos DEPOIS que a query de execuções resolve
  // (`executionsLoading`). Antes do fix, a lista vinha `[]` por um instante e o
  // drawer abria em TODA navegação — mesmo com runs concluídas. O ref garante
  // abertura única por montagem; toggles do usuário usam setConfigSheetOpen.
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    // FA não tem run pelo form (roda só pela Varredura da base) — nunca
    // auto-abre o drawer de config dele.
    if (agentId === "architect") return;
    const open = shouldAutoOpenConfigDrawer({
      alreadyOpened: autoOpenedRef.current,
      executionsLoading,
      executionsCount: executions.length,
      isViewingPastRun,
      executionStatus: execution.status,
      isLocked,
    });
    if (!open) return;
    autoOpenedRef.current = true;
    setConfigSheetOpen(true);
  }, [
    agentId,
    executionsLoading,
    executions.length,
    isViewingPastRun,
    execution.status,
    isLocked,
  ]);

  // Live elapsed timer (workbench hero). Drives a 1-second re-render so
  // the "Elapsed 0:42" counter increments while a run is active.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!isRunning) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [isRunning]);

  // Hero meta.
  const heroRunState = heroState.runState;
  const heroRunId =
    execution.executionId?.slice(0, 5) ?? latestCompleted?.id.slice(0, 5) ?? null;
  // #853: o decorrido só sai da execução quando é ela que está descrevendo o
  // cabeçalho. Com a varredura rodando, o print de 30/09 mostrava `Elapsed
  // 0:48` — a latência da execução que já tinha acabado — ao lado de um
  // trabalho estimado em duas horas e meia.
  const heroElapsed = !heroState.usesExecutionElapsed && backgroundTookOver
    ? "—"
    : isRunning && execution.startedAt
      ? formatDurationMs(Date.now() - execution.startedAt)
      : resolvedMetrics?.latency_ms != null
        ? formatDurationMs(resolvedMetrics.latency_ms)
        : "0:00";
  const heroCurrent = useMemo(() => {
    if (backgroundTookOver) return heroState.current;
    if (isRunning) {
      const idx = execution.phaseIndex;
      if (idx >= 0 && phases && phases[idx]) return phases[idx];
      return execution.phaseMessage || execution.phase || t("processing");
    }
    if (execution.status === "paused") return t("Paused — awaiting input");
    if (isComplete || executionDone) return t("Ready");
    if (isLocked) return t("Waiting upstream");
    return t("Standby");
  }, [t, backgroundTookOver, heroState.current, isRunning, execution.phaseIndex, execution.phaseMessage, execution.phase, execution.status, phases, isComplete, executionDone, isLocked]);
  const heroModel =
    (resolvedMetrics as Record<string, unknown> | null | undefined)?.model as
      | string
      | undefined;

  // Workbench pipeline nodes — derive status from execution.phaseIndex and
  // run-level flags. Unlike Functional, FA's nodes are NOT clickable: outputs
  // are cross-pipeline (Catálogo WRICEF aggregates results from many nodes),
  // so jumping to a node doesn't change what's displayed below.
  const pipelineNodes: PipelineNode[] = useMemo(() => {
    if (!phases || phases.length === 0) return [];
    const phaseIdx = execution.phaseIndex;
    const isPaused = execution.status === "paused";
    const isFailed = execution.status === "failed";
    return phases.map((name, i) => {
      let status: PipelineNodeStatus = "queued";
      if (isRunning) {
        if (i < phaseIdx) status = "complete";
        else if (i === phaseIdx) status = "running";
        else status = "queued";
      } else if (isPaused) {
        if (i < phaseIdx) status = "complete";
        else if (i === phaseIdx) status = "paused";
        else status = "queued";
      } else if (isFailed) {
        if (i < phaseIdx) status = "complete";
        else if (i === phaseIdx) status = "error";
        else status = "queued";
      } else if (isComplete || executionDone || isViewingPastRun) {
        status = "complete";
      }
      return {
        id: `phase-${i}`,
        shortName: name,
        status,
        tag:
          status === "running"
            ? "Now running"
            : status === "complete"
              ? `Node ${String(i + 1).padStart(2, "0")}`
              : status === "error"
                ? "Errored"
                : status === "paused"
                  ? "Paused"
                  : "Pending",
      };
    });
  }, [phases, execution.phaseIndex, execution.status, isRunning, isComplete, executionDone, isViewingPastRun]);

  return (
    <div className="flex gap-0 -m-6">
      <div className="flex-1 space-y-4 relative">
        <HeroSection padding="compact">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-[11px] text-white/70 hover:text-white hover:bg-white/10 gap-1 -ml-2 mb-2"
            onClick={() => navigate(`/projects/${projectId}`)}
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {t("Back to Project")}
          </Button>
          <div className="flex justify-between items-start gap-6 flex-wrap">
            <div className="flex items-start gap-3.5">
              {agentNumber != null && (
                <CompactAgentGlyph number={agentNumber} live={isRunning} />
              )}
              <div>
                <HeroOverline live={isRunning} className="mb-1.5">
                  {agentNumber != null && totalAgents != null
                    ? t("AGENT {{num}} OF {{total}}", {
                        num: String(agentNumber).padStart(2, "0"),
                        total: String(totalAgents).padStart(2, "0"),
                      })
                    : t("AGENT")}
                  {` · ${t(heroRunState)}`}
                  {heroRunId && ` · ${t("RUN #{{id}}", { id: heroRunId.toUpperCase() })}`}
                </HeroOverline>
                <h1 className="font-brand font-extrabold text-[26px] leading-[1.05] tracking-[-0.025em] text-white m-0 mb-2 flex items-center gap-2.5 flex-wrap">
                  {agentName}
                  {agentBadge && (
                    <span
                      className="font-mono text-[9px] font-semibold px-2 py-0.5 rounded-full border border-white/15 text-white/80 uppercase tracking-[0.08em]"
                      style={{ background: "rgba(255,255,255,0.08)" }}
                    >
                      {agentBadge}
                    </span>
                  )}
                </h1>
                <p className="text-[12px] text-white/60 mb-2 max-w-2xl leading-relaxed">
                  {description}
                </p>
                <div className="flex items-center gap-3 flex-wrap text-[11px] text-white/55 font-mono tracking-[0.04em]">
                  {phaseLabel && (
                    <>
                      <HeroMeta label={t("Phase")} value={phaseLabel} />
                      <HeroDot />
                    </>
                  )}
                  {phases && phases.length > 0 && (
                    <>
                      <HeroMeta
                        label={t("Pipeline")}
                        value={tp(t, phases.length, "{{n}} node", "{{n}} nodes")}
                      />
                      <HeroDot />
                    </>
                  )}
                  <HeroMeta label={t("Current")} value={heroCurrent} />
                  <HeroDot />
                  <HeroMeta label={t("Elapsed")} value={heroElapsed} />
                  {heroModel && (
                    <>
                      <HeroDot />
                      <HeroMeta label={t("Model")} value={heroModel} />
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-1.5 items-center shrink-0">
              {/* FA roda em fases (full-base); o histórico relevante são os
                  SCANS (jobs), não as execuções por fase. Os demais agentes
                  seguem com o histórico de execuções genérico. */}
              {agentId === "architect" ? (
                projectId && (
                  <ScanHistory
                    projectId={projectId}
                    triggerTone="dark"
                    selectedScanId={selectedScanId}
                    onSelectScan={setSelectedScanId}
                  />
                )
              ) : (
                executions.length > 0 && (
                  <ExecutionHistory
                    executions={executions}
                    selectedExecutionId={effectiveSelectedId}
                    latestExecutionId={latestCompleted?.id ?? null}
                    onSelect={setSelectedExecutionId}
                    triggerTone="dark"
                  />
                )
              )}
              {/* O chat tem entrada única: o pill do FloatingChat (canto
                  inferior direito), que abre/minimiza/fecha sozinho. O botão
                  Chat no header era um 2º gatilho redundante — abria o painel
                  longe do botão (parecia sem ação). Removido pra um caminho só. */}
              {!isLocked && !isViewingPastRun && agentId !== "architect" && (
                // Um único CTA: "Configure" e "New Run" abriam o MESMO drawer
                // (setConfigSheetOpen) — dois botões pra uma ação só confundiam.
                // O drawer já tem o botão de rodar; aqui basta abri-lo.
                // O architect NÃO tem run pelo form: roda só pela
                // "Varredura da base ABAP" (full-base). Esconder o CTA evita a
                // ambiguidade de dois caminhos pro mesmo agente.
                <Button
                  type="button"
                  className="h-[30px] gap-1.5 px-3 text-[11px] font-semibold bg-[#ffc83d] hover:bg-[#ffd96a] text-[#08254c] border-0"
                  onClick={() => setConfigSheetOpen(true)}
                  disabled={isRunning}
                >
                  <Zap className="w-3 h-3" />
                  {isComplete || executionDone ? t("New Run") : t("Run agent")}
                </Button>
              )}
            </div>
          </div>
        </HeroSection>

      {/* Side padding only — full-bleed width so the catalog can spread to
          all available horizontal space (matching the SKU Mapper Workbench
          feel). */}
      <div className="space-y-4 px-6 pb-6 pt-4">

      {isLocked && pipelineAgent && (
        <AgentLockBanner dependencyIds={pipelineAgent.depends_on} />
      )}
      <ArtifactChain projectId={projectId!} agentId={agentId} />

      {/* Past run banner */}
      {isViewingPastRun && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-2.5 flex items-center justify-between">
          <span className="text-xs text-amber-500">
            {t("Viewing past run from")}{" "}
            <strong>
              {formatDateTime(selectedExecution!.started_at)}
            </strong>
          </span>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs gap-1"
            onClick={() => setSelectedExecutionId(null)}
          >
            <RotateCcw className="h-3 w-3" />
            {t("Back to Latest")}
          </Button>
        </div>
      )}

      {!isViewingPastRun && (
        <AgentStatusBanner
          status={status}
          agentName={agentName}
          detail={statusDetail}
        />
      )}

      {/* Single column, full width. */}
      <div className="space-y-4">
        {/* O architect roda em fases (full-base) cuja progressão é
            mostrada pelo painel "Varredura da base ABAP". O pipeline-view de
            nós aqui (legado do run single-pass) não reflete o full-base e só
            confunde — escondido pro FA. */}
        {agentId === "architect" ? null : pipelineNodes.length > 0 ? (
          <div className="-mx-6">
            <PipelineProgress nodes={pipelineNodes} />
          </div>
        ) : (
          <ExecutionStepper
            currentPhase={
              execution.phaseIndex >= 0
                ? execution.phaseIndex
                : (isComplete || executionDone || isViewingPastRun)
                  ? (phases?.length ?? 6)
                  : -1
            }
            phases={phases}
          />
        )}

        <WarningsBanner warnings={resolvedWarnings} />

        {!renderOutputInTabs && showOutput && (
          <>
            <StreamingOutput content={reportContent} isStreaming={isRunning} />
            {/* Execution Metrics é por-execução (modelo do form-run). Pro FA
                full-base ele reflete a última fase (synthesis = 0 tokens/custo)
                e fica zerado/enganoso — as métricas reais do scan vivem no
                painel da Varredura + histórico. Escondido pro FA. */}
            {agentId !== "architect" &&
              !isRunning &&
              (executionDone || isComplete) &&
              resolvedMetrics && <ExecutionMetrics metrics={resolvedMetrics} />}
          </>
        )}

        <AgentExecutionProvider value={executionContextValue}>
          {children}
        </AgentExecutionProvider>

        {/* FollowUpChat is replaced by FloatingChat rendered below at the
            column root for proper absolute positioning. */}
      </div>
      </div> {/* /content padding wrapper */}

      {/* Floating chat anchored to the main column. */}
      <FloatingChat
        mode={chatMode}
        setMode={setChatMode}
        projectId={projectId!}
        agentId={agentId}
        agentLabel={agentName}
        // FA: sem execution_id → conversa estável por (projeto, agente); o
        // contexto (output_summary) usa o último scan no backend. Pros demais,
        // segue por execução.
        executionId={
          agentId === "architect"
            ? undefined
            : resolvedExecutionId || undefined
        }
        isComplete={isComplete || executionDone}
      />
      </div> {/* /flex-1 main column */}

      {/* Config form lives in this side drawer */}
      <Sheet open={configSheetOpen} onOpenChange={setConfigSheetOpen}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{agentName}</SheetTitle>
            <SheetDescription>
              {t("Configure the parameters and trigger a new run.")}
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4">
            <AgentInputForm
              schema={schema?.inputs || []}
              schemaLoaded={!!schema}
              preparedInputs={formPreparedInputs}
              preparedSources={preparedInputs?.sources || []}
              agentId={agentId}
              isRunning={isRunning}
              isComplete={isComplete || executionDone}
              isLocked={isLocked}
              onRun={handleRun}
              onCancel={handleCancel}
              projectId={projectId}
            />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function HeroMeta({ label, value }: { label: string; value: string }) {
  return (
    <span>
      <span className="text-white/40 mr-1">{label}</span>
      <strong className="text-white/90 font-medium">{value}</strong>
    </span>
  );
}

function HeroDot() {
  return <span className="text-white/25">·</span>;
}

function formatDurationMs(ms: number): string {
  const totalSec = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}
